import { Checkbox, Combobox, Select, TextField } from '@kcm/ui'
import type { AmlValue, FormField } from '@kcm/contracts'

/**
 * Renders a form the funnel does not own (PLAN.md §27.3).
 *
 * The AML questionnaire lives in the CRM, and compliance changes it without
 * asking us. So this component knows about controls, not about questions: it
 * never names a field, never special-cases one, and adding a declaration in the
 * CRM shows up here with no deploy.
 *
 * Everything CRM-shaped has already been resolved by the API — labels are
 * sanitised plain text, `meta.set` never leaves the server, and a `text` field
 * carrying `choices` arrives as `select`. That is why there is no HTML escaping
 * or type-guessing below: doing it here would mean every future renderer has to
 * remember to.
 */

export interface DynamicFormProps {
  fields: FormField[]
  values: Record<string, AmlValue>
  onChange: (key: string, value: AmlValue) => void
  /** Per-field messages from the server, keyed exactly as the fields are. */
  errors?: Record<string, string[]> | undefined
  disabled?: boolean
}

/**
 * Above this, a native picker is a scroll rather than a choice — Thailand's
 * declaration asks for one of 77 provinces. Below it, the platform control
 * wins for all the reasons `Select` documents.
 */
const SEARCHABLE_FROM = 12

/** Below this many declarations, a master tick is more chrome than help. */
const TICK_ALL_FROM = 3

export function DynamicForm({ fields, values, onChange, errors, disabled }: DynamicFormProps) {
  // Every checkbox, not only the ones flagged required (changed 2026-09-08).
  // The master tick used to skip the CRM's marketing-consent box so that
  // consent stayed a separate decision; the business now requires the whole
  // declaration to be accepted as one, so "agree to all" means all of it.
  // Filtering on type rather than on `required` keeps that true even if the
  // server ever marks a box optional again — the two would otherwise disagree.
  const ticks = fields.filter((f) => f.type === 'checkbox')
  const allTicked = ticks.length > 0 && ticks.every((f) => values[f.key] === true)

  return (
    <div className="flex flex-col gap-4">
      {ticks.length >= TICK_ALL_FROM && (
        <div className="rounded-lg border border-line-strong bg-surface-alt p-3">
          <Checkbox
            label={`I have read and agree to all ${ticks.length} declarations below`}
            checked={allTicked}
            disabled={disabled}
            onChange={(e) => {
              for (const f of ticks) onChange(f.key, e.target.checked)
            }}
          />
        </div>
      )}
      {fields.map((field) => {
        const error = errors?.[field.key]?.[0]
        const value = values[field.key]

        if (field.type === 'checkbox') {
          // No "Optional" tag any more: every box the CRM sends is mandatory,
          // so a badge that could never appear would only mislead the next
          // reader. `required` is still passed through as the server sent it.
          return (
            <Checkbox
              key={field.key}
              label={field.label}
              checked={value === true}
              required={field.required}
              disabled={disabled}
              {...(error ? { error } : {})}
              onChange={(e) => onChange(field.key, e.target.checked)}
            />
          )
        }

        const text = typeof value === 'string' ? value : ''

        if (field.type === 'select') {
          const choices = field.choices ?? []
          return choices.length >= SEARCHABLE_FROM ? (
            <Combobox
              key={field.key}
              label={field.label}
              options={choices}
              value={text}
              required={field.required}
              error={error}
              {...(disabled ? { disabled } : {})}
              hint="Start typing to search"
              onChange={(next) => onChange(field.key, next)}
            />
          ) : (
            <Select
              key={field.key}
              label={field.label}
              options={choices.map((c) => ({ value: c, label: c }))}
              placeholder="Select an answer"
              value={text}
              required={field.required}
              disabled={disabled}
              error={error}
              onChange={(e) => onChange(field.key, e.target.value)}
            />
          )
        }

        return (
          <TextField
            key={field.key}
            label={field.label}
            // Off, like the Combobox above: these are the CRM's own compliance
            // questions, labelled by whoever configured them. No autofill token
            // describes "source of funds", so a browser filling one in is
            // guessing — and the guess would be filed as a regulatory answer
            // the user never gave.
            autoComplete="off"
            value={text}
            required={field.required}
            disabled={disabled}
            error={error}
            onChange={(e) => onChange(field.key, e.target.value)}
          />
        )
      })}
    </div>
  )
}

/**
 * Which required fields are still unanswered.
 *
 * The server decides what counts (it re-validates everything), but the button
 * should not send a submission that is going to come back with six errors on
 * it. A checkbox has to be ticked; a text or select answer has to be non-empty.
 */
export function missingRequired(
  fields: FormField[],
  values: Record<string, AmlValue>,
): string[] {
  return fields
    .filter((field) => {
      if (!field.required) return false
      const value = values[field.key]
      return field.type === 'checkbox' ? value !== true : typeof value !== 'string' || value === ''
    })
    .map((field) => field.key)
}
